/**
 * Jain Gaurav Yatra — Rickshaws CRUD API.
 * Backend: /jain_gaurav_yatra/rickshaws/  (cookie-session auth + X-Tenant-ID, JSON body).
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function jsonHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg =
    pick(d?.message) ??
    pick(d?.detail) ??
    (Array.isArray(d?.non_field_errors) ? pick((d.non_field_errors as string[])[0]) : null);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const RICKSHAWS_PATH = "/jain_gaurav_yatra/rickshaws/";

/** Page-facing rickshaw shape (matches the existing admin.rickshaws UI fields). */
export interface Rickshaw {
  id: string;
  driver_name: string;
  phone: string;
  vehicle_number: string;
  area: string;
  charges_per_trip: number;
  active: boolean;
}

/** Fields accepted when creating/updating a rickshaw. */
export interface RickshawWrite {
  driver_name: string;
  phone: string;
  vehicle_number: string;
  area: string;
  charges_per_trip: number;
  active: boolean;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function toRickshaw(row: Record<string, unknown>): Rickshaw {
  return {
    id: str(row.id),
    driver_name: str(row.driver_name),
    phone: str(row.phone),
    vehicle_number: str(row.vehicle_number),
    area: str(row.area),
    charges_per_trip: num(row.charges_per_trip),
    active: row.active !== false,
  };
}

function toBody(p: RickshawWrite): string {
  return JSON.stringify({
    driver_name: p.driver_name,
    phone: p.phone,
    vehicle_number: p.vehicle_number,
    area: p.area,
    charges_per_trip: p.charges_per_trip,
    active: p.active,
  });
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

export async function listRickshaws(): Promise<Rickshaw[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${RICKSHAWS_PATH}`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return (rows as Record<string, unknown>[]).map(toRickshaw);
}

export async function createRickshaw(payload: RickshawWrite): Promise<Rickshaw> {
  const base = apiBase();
  const res = await fetch(`${base}${RICKSHAWS_PATH}`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toRickshaw(data as Record<string, unknown>);
}

export async function updateRickshaw(id: string, payload: RickshawWrite): Promise<Rickshaw> {
  const base = apiBase();
  const res = await fetch(`${base}${RICKSHAWS_PATH}${id}/`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toRickshaw(data as Record<string, unknown>);
}

export async function deleteRickshaw(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${RICKSHAWS_PATH}${id}/`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const data = await parseJson(res);
    throw new Error(errMessage(data, res.status));
  }
}
